/**
 * The suggester, wired to your ledger.
 *
 * Training is **lazy**: nothing is read or trained until a sheet that suggests
 * says it wants suggestions (`wantSuggestions()`), and then not again until the
 * ledger or the settings change. Opening the app trains nothing. Learning is
 * one of the few things that really must see every filed row, so the rows are
 * asked for then — only the filed ones, only the columns it learns from.
 *
 * Only tags put on a row directly are learned. A tag inherited from a category
 * would teach the model to predict the category twice over, once under its own
 * name and once through everything it hands down.
 */
import { computed, ref } from 'vue'
import { query } from '../db'
import { DEFAULTS, FEATURE_DEFAULTS, evaluate, train } from '../lib/suggest'
import { setSetting } from './settings'
import { state } from './state'
import { fromDb, settled } from './live'

/** The stored settings over the defaults. Experiments survive a reload. */
export const suggestSettings = computed(() => ({
  ...DEFAULTS,
  ...FEATURE_DEFAULTS,
  ...(state.settings.suggest ?? {}),
}))

export const setSuggestSetting = (key, value) =>
  setSetting('suggest', { ...(state.settings.suggest ?? {}), [key]: value })

export const resetSuggestSettings = () => setSetting('suggest', {})

const wanted = ref(false)

/** Starts the learning — a sheet about to suggest calls it as it opens. */
export function wantSuggestions() {
  wanted.value = true
}

/** Resolves once the rows are read and a suggestion can be asked for. */
export async function suggestionsReady() {
  wantSuggestions()
  await settled()
}

/**
 * Rows worth learning from: the ones you have actually filed. Newest first,
 * as the ledger lists them — `evaluate()` holds out the most recent slice.
 */
export const trainingSet = fromDb(
  () => (wanted.value ? {} : null),
  async () => {
    const rows = await query(
      `SELECT t.description, t.amount, t.date, COALESCE(c.name, '') AS category,
              (SELECT group_concat(tag_id) FROM transaction_tags WHERE transaction_id = t.id) AS own_tags
         FROM transactions t LEFT JOIN categories c ON c.id = t.category_id
        WHERE c.id IS NOT NULL OR EXISTS (SELECT 1 FROM transaction_tags x WHERE x.transaction_id = t.id)
        ORDER BY t.date DESC, t.created_at DESC`,
    )
    return rows.map((r) => ({
      tx: { description: r.description, amount: r.amount, date: r.date },
      category: r.category || '',
      tags: r.own_tags ? String(r.own_tags).split(',') : [],
    }))
  },
  [],
)

export const model = computed(() => train(trainingSet.value, suggestSettings.value))

/** How the current settings do on rows the model was not trained on. */
export const modelQuality = computed(() => evaluate(trainingSet.value, suggestSettings.value))

/** Whether there is enough history for any of this to mean anything. */
export const canSuggest = computed(() => trainingSet.value.length >= 8)

/**
 * What this transaction probably is, as names and tag ids the UI can show.
 * Silent — not wrong — when there is nothing to go on.
 */
export function suggestionsFor(tx) {
  if (!canSuggest.value || !tx?.description) return { category: null, tags: [] }
  const guess = model.value.suggest(tx)
  return {
    category: guess.category,
    // A tag that has since been deleted is not a suggestion any more.
    tags: guess.tags.filter((t) => state.tags.some((tag) => tag.id === t.id)),
  }
}

/** The words behind a suggestion, for the lab and for anyone who asks why. */
export const whySuggested = (tx, label, kind) => model.value.explain(tx, label, kind)
